import { useEffect, useState } from "react";
import { useAssignedClientSelection, useBusyAction, useRequest } from "../../../hooks";
import { DownloadAction } from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";

const isSuccessful = (response: any) => response?.success || response?.succes;

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

export const AdditionalDocumentsClient = () => {
  const { handleRequest } = useRequest();
  const {
    selectedClientId,
    clientOptions,
    showClientFilter,
    setSelectedClientId,
  } = useAssignedClientSelection();
  const { paginationModel, setPaginationModel } = usePaginator("additional-documents-client", 8);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    if (!selectedClientId) return;
    setRowsLoading(true);
    handleRequest({
      endpoint: `additional-document-list/client/${selectedClientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        const docs: DocRow[] = (isSuccessful(response) ? response.data : []).map(
          (item: any) => ({
            rowId: `ad-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellName: "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
            mimeType: item.mimeType,
            originalFileName: item.originalFileName,
          }),
        );
        setRows(docs);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  }, [selectedClientId]);

  const downloadFile = async (row: DocRow) => {
    if (!row.path) return;
    const response = await handleRequest({
      endpoint: "additional-document-download",
      options: {
        method: "POST",
        body: JSON.stringify({ key: row.path }),
        headers: { responseType: "Buffer" } as any,
      },
    });
    if (!response.success) {
      setIsError(true);
      return;
    }
    const mimeType = response.mimeType || row.mimeType || "application/octet-stream";
    const url = window.URL.createObjectURL(
      new Blob([new Uint8Array(response.data.data).buffer], { type: mimeType }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", row.originalFileName || row.customName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(downloadFile);

  // ---- Derived: search -> date filters ----
  let filtered = docSearch.trim()
    ? rows.filter((r) => r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()))
    : rows;
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.date + "T12:00:00");
      if (dateFrom && d < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Document", render: (r) => r.customName, width: "2fr" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
  ];

  const filters: ListFilterField[] = [
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];

  return (
    <ListPageLayout<DocRow>
      title="Additional Documents"
      subtitle="Browse, filter and download additional documents"
      columns={columns}
      rows={pageRows}
      getRowId={(r) => r.rowId}
      rowsLoading={rowsLoading}
      isError={isError}
      emptyTitle={
        !selectedClientId ? "Select a client to see additional documents" : undefined
      }
      searchValue={docSearch}
      onSearchChange={setDocSearch}
      searchPlaceholder="Search additional documents…"
      filters={filters}
      page={currentPage}
      pageSize={paginationModel.pageSize}
      totalCount={totalCount}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      rowsPerPageOptions={[8, 15, 25, 50]}
      toolbarActions={
        showClientFilter && clientOptions?.length ? (
          <select
            value={selectedClientId ? String(selectedClientId) : ""}
            onChange={(e) => {
              const c =
                clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
              setSelectedClientId(c?.id ?? null);
            }}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        ) : undefined
      }
      rowActions={(r) => (
        <DownloadAction rowId={r.rowId} busy={isDownloading} onClick={() => runDownload(r)} />
      )}
    />
  );
};
